import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Sparkles, Ribbon, Bug, AlertTriangle, ShieldCheck, Droplet,
  Stethoscope, Shield, MessageCircleHeart, Search, ArrowRight, HandHeart,
} from "lucide-react";
import { TOPICS, DISCLAIMER } from "../data/healthTopics";

const ICON_MAP = {
  Sparkles, Ribbon, Bug, AlertTriangle, ShieldCheck, Droplet,
  Stethoscope, Shield, MessageCircleHeart,
};

// Tone → Tailwind classes for the card icon chip.
const TONE_STYLES = {
  rose:   "bg-[#FFF0EC] text-[#C24C29] dark:bg-[#3A211B] dark:text-[#E07A5F]",
  red:    "bg-[#FDECEC] text-[#B4353A] dark:bg-[#3A1E20] dark:text-[#E88090]",
  amber:  "bg-[#FEF3D6] text-[#B45309] dark:bg-[#3A2E14] dark:text-[#E9B970]",
  orange: "bg-[#FFEBDC] text-[#B45309] dark:bg-[#3A2517] dark:text-[#E8A377]",
  violet: "bg-[#EEE7FA] text-[#6D4AA3] dark:bg-[#2A2140] dark:text-[#B7A0E6]",
  yellow: "bg-[#FFF6D6] text-[#8A6A08] dark:bg-[#3A3216] dark:text-[#E1CB74]",
  teal:   "bg-[#DDEFEA] text-[#20675A] dark:bg-[#1E322C] dark:text-[#7DC4B0]",
  sage:   "bg-[#E4EDDE] text-[#3E6A2C] dark:bg-[#243019] dark:text-[#9ABF7F]",
};

function TopicCard({ topic }) {
  const Icon = ICON_MAP[topic.icon] || Sparkles;
  const chip = TONE_STYLES[topic.tone] || TONE_STYLES.rose;
  return (
    <Link
      to={`/health/${topic.slug}`}
      data-testid={`health-topic-card-${topic.slug}`}
      className="group block focus:outline-none focus:ring-2 focus:ring-[#D95D39] focus:ring-offset-2 focus:ring-offset-[#FFFDF9] dark:focus:ring-offset-[#1A1615] rounded-3xl"
    >
      <Card className="h-full p-6 sm:p-7 rounded-3xl border-[#E8DEC8] bg-white dark:bg-[#221E1C] dark:border-[#423B36] transition-all group-hover:-translate-y-0.5 group-hover:shadow-[0_10px_30px_-12px_rgba(217,93,57,0.28)]">
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 shrink-0 rounded-2xl flex items-center justify-center ${chip}`}>
            <Icon className="w-5 h-5" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-[#8A7F78] dark:text-[#8F847C] mb-1">
              {topic.kicker}
            </div>
            <h3 className="font-serif text-xl font-bold text-[#2C2623] dark:text-[#FAF5F0] leading-tight">
              {topic.title}
            </h3>
            <p className="text-sm text-[#6E655F] dark:text-[#B5AAA1] mt-2 leading-relaxed">
              {topic.hubBlurb}
            </p>
            <div className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C24C29] dark:text-[#E07A5F] group-hover:gap-2.5 transition-all">
              Read guide <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>
      </Card>
    </Link>
  );
}

export default function Health() {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return TOPICS;
    return TOPICS.filter(
      (t) =>
        t.title.toLowerCase().includes(needle) ||
        t.subtitle.toLowerCase().includes(needle) ||
        t.hubBlurb.toLowerCase().includes(needle) ||
        t.kicker.toLowerCase().includes(needle)
    );
  }, [q]);

  return (
    <div className="min-h-screen bg-[#FFFDF9] dark:bg-[#1A1615]">
      <Navbar />
      <main
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14"
        data-testid="health-hub-page"
      >
        <div className="text-center mb-10 sm:mb-14" data-testid="health-hub-hero">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFF0EC] text-[#C24C29] text-sm font-medium mb-4 dark:bg-[#3A211B]">
            <HandHeart className="w-4 h-4" /> Health &amp; Resources
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#2C2623] dark:text-[#FAF5F0] tracking-tight">
            Sexual Health &amp; STI Hub
          </h1>
          <p className="text-base sm:text-lg text-[#6E655F] dark:text-[#B5AAA1] mt-4 max-w-2xl mx-auto leading-relaxed">
            Real information. No shame. No scare tactics. Learn about common sexually transmitted infections, testing, treatment, prevention, disclosure, and safer sex in plain language.
          </p>
        </div>

        <div className="max-w-xl mx-auto mb-10 sm:mb-12">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A7F78]" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search topics — e.g., HIV, PrEP, testing, disclosure"
              data-testid="health-hub-search-input"
              className="pl-11 h-12 rounded-full border-[#E8DEC8] bg-white dark:bg-[#221E1C] dark:border-[#423B36] dark:text-[#FAF5F0] focus-visible:ring-[#D95D39]"
            />
          </div>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
          data-testid="health-topics-grid"
        >
          {filtered.map((topic) => (
            <TopicCard key={topic.slug} topic={topic} />
          ))}
        </div>

        {filtered.length === 0 && (
          <div
            className="text-center py-16 text-[#6E655F] dark:text-[#B5AAA1]"
            data-testid="health-topics-empty"
          >
            No topics matched "{q}". Try another word.
          </div>
        )}

        <div
          className="mt-14 sm:mt-16 p-6 sm:p-7 rounded-3xl border border-[#E8DEC8] bg-[#FFF9EE] dark:bg-[#221E1C] dark:border-[#423B36]"
          data-testid="health-hub-disclaimer"
        >
          <div className="flex items-start gap-3">
            <HandHeart className="w-5 h-5 text-[#B45309] shrink-0 mt-0.5" />
            <p className="text-sm text-[#4A413B] dark:text-[#B5AAA1] leading-relaxed">
              {DISCLAIMER}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
